import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Calendar,
  Eye,
  EyeOff,
  Camera,
  Save,
  X,
  Edit2,
  Star,
  Award,
  Target,
  TrendingUp,
  Settings,
  LogOut,
  Crown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import './Profile.css';

interface EditableUser {
  fullName: string;
  email: string;
  bio?: string;
  location?: string;
  website?: string;
  avatar?: string;
}

interface NotificationSettings {
  emailUpdates: boolean;
  practiceReminders: boolean;
  achievementNotifications: boolean;
  weeklyProgress: boolean;
}

const Profile: React.FC = () => {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const [editedUser, setEditedUser] = useState<EditableUser>({
    fullName: state.user?.fullName || '',
    email: state.user?.email || '',
    bio: '',
    location: '',
    website: '',
    avatar: state.user?.avatar || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailUpdates: true,
    practiceReminders: true,
    achievementNotifications: true,
    weeklyProgress: false
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!state.isAuthenticated) {
      navigate('/login');
    }
  }, [state.isAuthenticated, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationChange = (key: keyof NotificationSettings) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveProfile = () => {
    // In a real app, this would make an API call
    console.log('Saving profile:', editedUser);
    setIsEditing(false);
  };

  const handleChangePassword = async () => {
    setPasswordStatus(null);
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'Enter your current password and choose a new password.' });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'The new passwords do not match.' });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setPasswordStatus({ type: 'error', message: 'Your new password must be at least 8 characters.' });
      return;
    }
    setIsUpdatingPassword(true);
    try {
      const response = await authService.updatePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordStatus({ type: 'success', message: response.message || 'Password updated successfully.' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setPasswordStatus({ type: 'error', message: error?.response?.data?.message || error?.message || 'Could not update your password. Please try again.' });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleAvatarChange = () => {
    // In a real app, this would open a file picker
    console.log('Change avatar');
  };

  const getSubscriptionBadge = () => {
    const subscription = state.user?.subscription || 'free';
    const badges = {
      free: { icon: User, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Free' },
      premium: { icon: Star, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Premium' },
      pro: { icon: Crown, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Pro' }
    };

    const badge = badges[subscription as keyof typeof badges];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {badge.label}
      </span>
    );
  };

  if (!state.user) {
    return null;
  }

  const isGoogleAccount = state.user.authProvider === 'google';

  return (
    <div className="profile-page min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="profile-hero-card bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="relative h-32 bg-gradient-to-r from-primary-600 to-primary-700">
            <div className="absolute inset-0 bg-black/20"></div>
          </div>

          <div className="relative px-6 pb-6">
            {/* Avatar */}
            <div className="relative -mt-16 mb-4">
              <div className="relative">
                {state.user.avatar ? (
                  <img
                    src={state.user.avatar}
                    alt={state.user.fullName}
                    className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 shadow-lg"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 shadow-lg bg-primary-600 flex items-center justify-center text-white text-4xl font-bold">
                    {state.user.fullName.charAt(0)}
                  </div>
                )}

                <button
                  onClick={handleAvatarChange}
                  className="absolute bottom-2 right-2 w-10 h-10 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  <Camera className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* User Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {state.user.fullName}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">{state.user.email}</p>
                <div className="mt-2 flex items-center space-x-4">
                  {/* DISABLED: Subscription badge — uncomment when subscription feature is ready
                  {getSubscriptionBadge()}
                  */}
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Joined {new Date(state.user.joinedDate).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="mt-4 sm:mt-0 flex space-x-3">
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </button>
                <button
                  onClick={logout}
                  className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs mt-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'profile', label: 'Profile Info', icon: User },
                { id: 'settings', label: 'Settings', icon: Settings },
                { id: 'stats', label: 'Statistics', icon: TrendingUp },
                { id: 'achievements', label: 'Achievements', icon: Award }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'profile' && (
            <div className="profile-panel bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Profile Information</h2>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Full Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="fullName"
                        value={editedUser.fullName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-white">{editedUser.fullName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        name="email"
                        value={editedUser.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-white">{editedUser.email}</p>
                    )}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bio
                  </label>
                  {isEditing ? (
                    <textarea
                      name="bio"
                      value={editedUser.bio}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Tell us about yourself..."
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white">{editedUser.bio || 'No bio added yet.'}</p>
                  )}
                </div>

                {/* Location & Website */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="location"
                        value={editedUser.location}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="City, Country"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-white">{editedUser.location || 'Not specified'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Website
                    </label>
                    {isEditing ? (
                      <input
                        type="url"
                        name="website"
                        value={editedUser.website}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="https://yourwebsite.com"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-white">
                        {editedUser.website ? (
                          <a href={editedUser.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                            {editedUser.website}
                          </a>
                        ) : (
                          'Not specified'
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {isEditing && (
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={handleSaveProfile}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="inline-flex items-center px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                )}

                {/* Password Change Section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Password</h3>
                    {isGoogleAccount ? (
                      <span className="profile-provider-badge">Managed by Google</span>
                    ) : (
                      <button
                        onClick={() => { setShowPasswordChange(!showPasswordChange); setPasswordStatus(null); }}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Change Password
                      </button>
                    )}
                  </div>

                  {isGoogleAccount ? (
                    <div className="profile-sso-password-note">
                      <strong>You signed in with Google</strong>
                      <p>Your sign-in password is managed by Google. Change it from your Google Account security settings; LetsBoostUp does not store or display it.</p>
                    </div>
                  ) : showPasswordChange && (
                    <div className="profile-password-panel space-y-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Current Password
                        </label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            name="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex space-x-3">
                        {passwordStatus && <p className={`profile-password-status profile-password-status--${passwordStatus.type}`}>{passwordStatus.message}</p>}
                        <button
                          onClick={handleChangePassword}
                          disabled={isUpdatingPassword}
                          className="profile-primary-button px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
                        >
                          {isUpdatingPassword ? 'Updating…' : 'Update Password'}
                        </button>
                        <button
                          onClick={() => { setShowPasswordChange(false); setPasswordStatus(null); setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' }); }}
                          className="profile-secondary-button px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="profile-panel bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Notification Settings</h2>

              <div className="space-y-4">
                {Object.entries(notifications).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        {key === 'emailUpdates' && 'Email Updates'}
                        {key === 'practiceReminders' && 'Practice Reminders'}
                        {key === 'achievementNotifications' && 'Achievement Notifications'}
                        {key === 'weeklyProgress' && 'Weekly Progress Reports'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {key === 'emailUpdates' && 'Receive updates about new features and content'}
                        {key === 'practiceReminders' && 'Get reminded to practice regularly'}
                        {key === 'achievementNotifications' && 'Be notified when you earn achievements'}
                        {key === 'weeklyProgress' && 'Receive weekly summaries of your progress'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleNotificationChange(key as keyof NotificationSettings)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="profile-panel bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Your Statistics</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Target className="w-8 h-8 text-primary-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Problems Solved</p>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">127</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center">
                    <TrendingUp className="w-8 h-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Streak</p>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">15 days</p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Award className="w-8 h-8 text-yellow-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Achievements</p>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">8</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="profile-panel bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Achievements</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { title: 'First Steps', description: 'Solved your first problem', icon: Target, earned: true },
                  { title: 'Week Warrior', description: 'Practiced for 7 consecutive days', icon: Calendar, earned: true },
                  { title: 'Problem Solver', description: 'Solved 50 problems', icon: Award, earned: true },
                  { title: 'Speed Demon', description: 'Solved a problem in under 5 minutes', icon: TrendingUp, earned: false },
                  { title: 'Knowledge Seeker', description: 'Completed all topics', icon: Star, earned: false },
                  { title: 'Master Coder', description: 'Solved 500 problems', icon: Crown, earned: false },
                ].map((achievement, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${achievement.earned
                      ? 'border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                      }`}
                  >
                    <div className="flex items-center mb-2">
                      <achievement.icon
                        className={`w-6 h-6 ${achievement.earned ? 'text-primary-600' : 'text-gray-400'
                          }`}
                      />
                      <h3 className={`ml-2 font-medium ${achievement.earned ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                        }`}>
                        {achievement.title}
                      </h3>
                    </div>
                    <p className={`text-sm ${achievement.earned ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400'
                      }`}>
                      {achievement.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
